import React, { useState, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { fetchAuth } from '../../utils/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function ClosingMarginView({ user, handleApiError, showNotification }) {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAuth(`/api/reports/closing-margin?month=${selectedMonth}&year=${selectedYear}`);
            if (res.ok) {
                setReportData(await res.json());
            } else {
                const err = await res.json();
                throw new Error(err.message || 'Failed to fetch report.');
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear, handleApiError]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    if (!user || user.role !== 'Admin') {
        return <div className="access-denied-container"><h2>Access Denied</h2><p>Only administrators can view this report.</p></div>;
    }

    const handleEditOpeningStock = async () => {
        const currentVal = reportData?.isOpeningStockOverridden ? reportData.openingStockValue : '';
        const input = window.prompt(
            "Enter manual Opening Stock value for this month.\nLeave blank to reset to auto-calculated value from actual product stock.", 
            currentVal
        );
        
        if (input === null) return; // User cancelled
        
        try {
            setLoading(true);
            const body = { month: selectedMonth, year: selectedYear, openingStockValue: input.trim() };
            const res = await fetchAuth('/api/reports/closing-margin-override', {
                method: 'POST',
                body: JSON.stringify(body)
            });
            if (res.ok) {
                showNotification(input.trim() === '' ? 'Opening Stock reverted to auto-calculation.' : 'Opening Stock override saved.', 'success');
                fetchReport();
            } else {
                const err = await res.json();
                throw new Error(err.message || 'Failed to save override');
            }
        } catch (error) {
            handleApiError(error);
            setLoading(false);
        }
    };

    const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];
    const years = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(y);

    // ── Chart Configs ──
    const trendChartData = reportData?.trendData ? {
        labels: reportData.trendData.map(d => d.month),
        datasets: [
            { label: 'Sales (₹)', data: reportData.trendData.map(d => d.sales), backgroundColor: 'rgba(99, 102, 241, 0.8)', borderRadius: 6 },
            { label: 'COGS (₹)', data: reportData.trendData.map(d => d.cogs), backgroundColor: 'rgba(239, 68, 68, 0.7)', borderRadius: 6 },
            { label: 'Purchases (₹)', data: reportData.trendData.map(d => d.purchases), backgroundColor: 'rgba(245, 158, 11, 0.7)', borderRadius: 6 },
        ]
    } : null;

    const trendChartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'top' }, title: { display: true, text: '6-Month Margin Trend', font: { size: 14 } } },
        scales: { x: { grid: { display: false } }, y: { beginAtZero: true } }
    };

    const topItemsChartData = reportData?.topItems?.length > 0 ? {
        labels: reportData.topItems.map(i => i.name?.length > 25 ? i.name.substring(0, 22) + '...' : i.name),
        datasets: [{
            label: 'Qty Sold',
            data: reportData.topItems.map(i => i.qtySold),
            backgroundColor: [
                'rgba(99, 102, 241, 0.8)', 'rgba(34, 197, 94, 0.8)', 'rgba(239, 68, 68, 0.8)',
                'rgba(245, 158, 11, 0.8)', 'rgba(168, 85, 247, 0.8)', 'rgba(236, 72, 153, 0.8)',
                'rgba(14, 165, 233, 0.8)', 'rgba(20, 184, 166, 0.8)', 'rgba(251, 146, 60, 0.8)',
                'rgba(139, 92, 246, 0.8)'
            ],
            borderRadius: 6,
        }]
    } : null;

    const topItemsChartOptions = {
        indexAxis: 'y', responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, title: { display: true, text: 'Top 10 Sold Items (by Qty)', font: { size: 14 } } },
        scales: { x: { beginAtZero: true, grid: { display: false } }, y: { grid: { display: false } } }
    };

    // ── Styles ──
    const cardGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' };
    const summaryCardStyle = (color) => ({
        background: `linear-gradient(135deg, ${color}15, ${color}08)`,
        border: `1px solid ${color}30`,
        borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '6px'
    });
    const cardLabelStyle = { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' };
    const cardValueStyle = (color) => ({ fontSize: '22px', fontWeight: 700, color });
    const sectionStyle = { background: 'var(--color-surface, #fff)', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
    const strategyCardStyle = { display: 'flex', gap: '14px', padding: '16px', borderRadius: '10px', background: 'var(--color-bg, #f8fafc)', marginBottom: '12px', border: '1px solid var(--color-border, #e2e8f0)' };

    return (
        <div className="page-container">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h2 className="page-title">Closing Margin Report</h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))} className="payment-select" style={{ minWidth: '140px' }}>
                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="payment-select" style={{ minWidth: '100px' }}>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
                    <p>Calculating your closing margin...</p>
                </div>
            ) : reportData ? (
                <>
                    {/* ── Summary Cards ── */}
                    <div style={cardGridStyle}>
                        <div style={summaryCardStyle('#6366f1')}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={cardLabelStyle}>Opening Stock</span>
                                <button 
                                    onClick={handleEditOpeningStock} 
                                    title="Edit Opening Stock"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#6366f1', opacity: 0.8 }}
                                >
                                    ✎
                                </button>
                            </div>
                            <span style={cardValueStyle('#6366f1')}>
                                {formatCurrency(reportData.openingStockValue)}
                                {reportData.isOpeningStockOverridden && <span style={{ fontSize: '12px', marginLeft: '6px', color: '#64748b', fontWeight: 'normal' }}>(Edited)</span>}
                            </span>
                        </div>
                        <div style={summaryCardStyle('#f59e0b')}>
                            <span style={cardLabelStyle}>Purchases</span>
                            <span style={cardValueStyle('#f59e0b')}>{formatCurrency(reportData.totalPurchases)}</span>
                        </div>
                        <div style={summaryCardStyle('#22c55e')}>
                            <span style={cardLabelStyle}>Sales Revenue</span>
                            <span style={cardValueStyle('#22c55e')}>{formatCurrency(reportData.totalSales)}</span>
                        </div>
                        <div style={summaryCardStyle('#ef4444')}>
                            <span style={cardLabelStyle}>Cost of Goods Sold</span>
                            <span style={cardValueStyle('#ef4444')}>{formatCurrency(reportData.cogs)}</span>
                        </div>
                        <div style={summaryCardStyle('#8b5cf6')}>
                            <span style={cardLabelStyle}>Closing Stock Value</span>
                            <span style={cardValueStyle('#8b5cf6')}>{formatCurrency(reportData.closingStockValue)}</span>
                        </div>
                        <div style={summaryCardStyle(reportData.grossMarginPercent >= 20 ? '#22c55e' : reportData.grossMarginPercent >= 10 ? '#f59e0b' : '#ef4444')}>
                            <span style={cardLabelStyle}>Gross Margin</span>
                            <span style={cardValueStyle(reportData.grossMarginPercent >= 20 ? '#22c55e' : reportData.grossMarginPercent >= 10 ? '#f59e0b' : '#ef4444')}>
                                {reportData.grossMarginPercent?.toFixed(1)}%
                            </span>
                        </div>
                    </div>

                    {/* ── Charts Row ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                        {trendChartData && (
                            <div style={sectionStyle}>
                                <div style={{ height: '350px' }}>
                                    <Bar data={trendChartData} options={trendChartOptions} />
                                </div>
                            </div>
                        )}
                        {topItemsChartData && (
                            <div style={sectionStyle}>
                                <div style={{ height: '350px' }}>
                                    <Bar data={topItemsChartData} options={topItemsChartOptions} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Top Items Table ── */}
                    {reportData.topItems?.length > 0 && (
                        <div style={sectionStyle}>
                            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>🏆 Top Selling Items — {reportData.period}</h3>
                            <div className="inventory-table-container">
                                <table className="inventory-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Item Name</th>
                                            <th>Qty Sold</th>
                                            <th>Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.topItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td>{item.name}</td>
                                                <td>{item.qtySold}</td>
                                                <td>{formatCurrency(item.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── Profit Strategies ── */}
                    {reportData.strategies?.length > 0 && (
                        <div style={sectionStyle}>
                            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>💡 Profit-Making Strategies</h3>
                            {reportData.strategies.map((strategy, idx) => (
                                <div key={idx} style={strategyCardStyle}>
                                    <div style={{ fontSize: '28px', lineHeight: 1 }}>{strategy.icon}</div>
                                    <div>
                                        <h4 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: 'var(--color-text, #1e293b)' }}>{strategy.title}</h4>
                                        <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: 'var(--color-text-muted, #64748b)' }}>{strategy.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                    <p>No data available for this period.</p>
                </div>
            )}
        </div>
    );
}

export default ClosingMarginView;
